/**
 * Hard-exception summary (AI Reviews tab).
 *
 * Backend: eastview-os run-agent-reviews.md "Hard-exception status block".
 * Every hard-exception run (button OR the lifecycle auto-trigger) writes a
 * definitive block onto the loan document — `loan.hardExceptionCheck`:
 *   { lastRunAt, status: clear | exceptions | marginal | pending,
 *     totals: { passed, failed, marginal, pending },
 *     failedChecks:   [{ key, label, expected, actual }],
 *     marginalChecks: [{ key, label, expected, actual }] }
 *
 * Per the doc, render this block directly rather than deriving hard-exception
 * state from the findings queue. Hidden when the loan has no block yet.
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import GppMaybeOutlined from "@mui/icons-material/GppMaybeOutlined";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";

const STATUS_STYLE = {
  clear: { label: "Clear", bg: "#ecfdf5", color: "#047857", accent: "#10b981" },
  exceptions: {
    label: "Hard Exceptions",
    bg: "#fef2f2",
    color: "#b91c1c",
    accent: "#dc2626",
  },
  marginal: {
    label: "Marginal",
    bg: "#fffbeb",
    color: "#b45309",
    accent: "#f59e0b",
  },
  pending: {
    label: "Pending",
    bg: "#f1f5f9",
    color: "#64748b",
    accent: "#94a3b8",
  },
};

const TOTALS = [
  { key: "passed", label: "Passed", color: "#047857" },
  { key: "failed", label: "Failed", color: "#b91c1c" },
  { key: "marginal", label: "Marginal", color: "#b45309" },
  { key: "pending", label: "Pending", color: "#64748b" },
];

const CheckRow = ({ check, accent }) => (
  <Flx
    sx={{
      gap: 1.5,
      alignItems: "flex-start",
      py: 1,
      px: 1.5,
      borderTop: "1px solid #f1f5f9",
      "&:first-of-type": { borderTop: "none" },
    }}
  >
    <Box
      component="span"
      sx={{
        mt: "5px",
        width: 6,
        height: 6,
        borderRadius: "999px",
        bgcolor: accent,
        flexShrink: 0,
      }}
    />
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: "#334155" }}>
        {check.label || check.key}
      </Txt>
      {(check.expected != null || check.actual != null) && (
        <Txt
          sx={{ fontSize: 11.5, color: "#94a3b8", display: "block", mt: "1px" }}
        >
          {check.actual != null && <>actual {String(check.actual)}</>}
          {check.expected != null && (
            <> · expected {String(check.expected)}</>
          )}
        </Txt>
      )}
    </Box>
  </Flx>
);

const AiHardExceptionPanel = () => {
  const block = useSelector((state) => state.loanDrilldown?.hardExceptionCheck);

  const st = STATUS_STYLE[block?.status] || STATUS_STYLE.pending;
  const failed = useMemo(
    () => (Array.isArray(block?.failedChecks) ? block.failedChecks : []),
    [block],
  );
  const marginal = useMemo(
    () => (Array.isArray(block?.marginalChecks) ? block.marginalChecks : []),
    [block],
  );
  const totals = block?.totals || {};

  // Hidden until a hard-exception run has written the block.
  if (!block || !block.status) return null;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e5e8ee",
        borderLeft: `3px solid ${st.accent}`,
        borderRadius: "14px",
        boxShadow:
          "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.08)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Flx
        jb
        sx={{
          alignItems: "flex-start",
          gap: 1.5,
          p: "18px 24px",
          borderBottom: failed.length || marginal.length ? "1px solid #eef1f5" : "none",
        }}
      >
        <Flx sx={{ gap: 1.5, alignItems: "flex-start", minWidth: 0 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "9px",
              bgcolor: st.bg,
              color: st.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <GppMaybeOutlined sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Flx ac sx={{ gap: 1, flexWrap: "wrap" }}>
              <Txt
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                  color: "#0f172a",
                }}
              >
                Hard Exception Check
              </Txt>
              <Chip
                size="small"
                label={st.label}
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: st.bg,
                  color: st.color,
                }}
              />
            </Flx>
            {block.lastRunAt && (
              <Txt
                sx={{
                  fontSize: 12,
                  color: "#94a3b8",
                  display: "block",
                  mt: "3px",
                }}
              >
                Last run {formatDateTime(block.lastRunAt)}
              </Txt>
            )}
          </Box>
        </Flx>

        {/* Totals */}
        <Flx sx={{ gap: 2, flexShrink: 0, flexWrap: "wrap" }}>
          {TOTALS.map((t) => (
            <Flx column key={t.key} sx={{ alignItems: "center", minWidth: 44 }}>
              <Txt sx={{ fontSize: 16, fontWeight: 700, color: t.color }}>
                {totals[t.key] ?? 0}
              </Txt>
              <Txt
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  color: "#94a3b8",
                }}
              >
                {t.label}
              </Txt>
            </Flx>
          ))}
        </Flx>
      </Flx>

      {/* Failed + marginal checks */}
      {(failed.length > 0 || marginal.length > 0) && (
        <Box sx={{ p: "10px 14px 14px" }}>
          {failed.length > 0 && (
            <Box sx={{ mb: marginal.length ? 1.5 : 0 }}>
              <Txt
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "#b91c1c",
                  px: 1.5,
                  mb: 0.5,
                  display: "block",
                }}
              >
                Failed ({failed.length})
              </Txt>
              {failed.map((c, i) => (
                <CheckRow key={c.key || i} check={c} accent="#dc2626" />
              ))}
            </Box>
          )}
          {marginal.length > 0 && (
            <Box>
              <Txt
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "#b45309",
                  px: 1.5,
                  mb: 0.5,
                  display: "block",
                }}
              >
                Marginal ({marginal.length})
              </Txt>
              {marginal.map((c, i) => (
                <CheckRow key={c.key || i} check={c} accent="#f59e0b" />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AiHardExceptionPanel;
